import { normalizeNewlines } from './promptDiff';

// 見出し行（# の後ろに空白が必要。# だけの行は空の見出し）
export const HEADING_PATTERN = /^(#{1,6})(?:\s+(.*))?$/;
export const FENCE_PATTERN = /^\s*```/;
// 箇条書き（- * + のいずれか＋空白）と番号付きリスト（数字. ＋空白）の行頭マーカー
const BULLET_PATTERN = /^(\s*)[-*+]\s+/;
const NUMBERED_PATTERN = /^(\s*)\d+\.\s+/;

// 行のリスト種別（'bullet' / 'numbered' / null）
export const listKindOf = (line) => {
    if (BULLET_PATTERN.test(line)) return 'bullet';
    if (NUMBERED_PATTERN.test(line)) return 'numbered';
    return null;
};

// 行頭のリストマーカーを外す（インデントは残す）
const stripListMarker = (line) => line.replace(BULLET_PATTERN, '$1').replace(NUMBERED_PATTERN, '$1');

// インデント・マーカー（- * + / 数字.）・マーカー後の空白に分ける
const LIST_ITEM_PATTERN = /^(\s*)([-*+]|(\d+)\.)(\s+)/;

// キャレットのある行（範囲選択なし・コードブロック外のリスト行）を調べる。対象外なら null
const caretListItem = (value, selStart, selEnd) => {
    if (selStart !== selEnd) return null;
    const lineStart = value.lastIndexOf('\n', selStart - 1) + 1;
    const nl = value.indexOf('\n', selStart);
    const lineEnd = nl < 0 ? value.length : nl;
    const m = value.slice(lineStart, lineEnd).match(LIST_ITEM_PATTERN);
    if (!m) return null;
    const fences = value.slice(0, lineStart).split('\n').filter((ln) => FENCE_PATTERN.test(ln)).length;
    if (fences % 2 === 1) return null;
    return { lineStart, lineEnd, indent: m[1], marker: m[2], number: m[3], space: m[4], markerEnd: lineStart + m[0].length };
};

/**
 * リスト行での Enter（一般的な Markdown エディタと同じ動き）
 * 次の行にも同じマーカーを続ける（番号付きは +1）。空の項目で押したらマーカーを消してリストを終える。
 * @returns {{text, selStart, selEnd}|null} null のときは通常の改行
 */
export const listEnter = (value, selStart, selEnd) => {
    const item = caretListItem(value, selStart, selEnd);
    if (!item || selStart < item.markerEnd) return null;
    if (!value.slice(item.markerEnd, item.lineEnd).trim()) {
        const text = value.slice(0, item.lineStart) + value.slice(item.lineEnd);
        return { text, selStart: item.lineStart, selEnd: item.lineStart };
    }
    const marker = item.number !== undefined ? (Number(item.number) + 1) + '.' : item.marker;
    const insert = '\n' + item.indent + marker + item.space;
    const pos = selStart + insert.length;
    return { text: value.slice(0, selStart) + insert + value.slice(selStart), selStart: pos, selEnd: pos };
};

// Tab で入れるインデント（半角スペース2つ）
export const INDENT = '  ';

/**
 * Tab / Shift+Tab のインデント
 * 複数行の選択は各行を、リスト行は行全体を字下げする。それ以外の Tab はキャレット位置に空白を入れる。
 * Shift+Tab は行頭の空白を最大 INDENT 分だけ戻す。
 * @returns {{text, selStart, selEnd}}
 */
export const tabIndent = (value, selStart, selEnd, outdent) => {
    const from = value.lastIndexOf('\n', selStart - 1) + 1;
    const multiLine = value.slice(selStart, selEnd).includes('\n');

    if (multiLine) {
        let to = selEnd;
        if (to > from && value[to - 1] === '\n') to -= 1;
        const nl = value.indexOf('\n', to);
        to = nl < 0 ? value.length : nl;
        const block = value.slice(from, to).split('\n')
            .map((ln) => (outdent ? ln.replace(new RegExp(`^ {1,${INDENT.length}}`), '') : INDENT + ln))
            .join('\n');
        return { text: value.slice(0, from) + block + value.slice(to), selStart: from, selEnd: from + block.length };
    }

    if (outdent) {
        const removed = (value.slice(from).match(new RegExp(`^ {1,${INDENT.length}}`)) || [''])[0].length;
        const shift = (pos) => Math.max(from, pos - removed);
        return { text: value.slice(0, from) + value.slice(from + removed), selStart: shift(selStart), selEnd: shift(selEnd) };
    }

    const nl = value.indexOf('\n', selStart);
    const line = value.slice(from, nl < 0 ? value.length : nl);
    if (selStart === selEnd && listKindOf(line)) {
        const pos = selStart + INDENT.length;
        return { text: value.slice(0, from) + INDENT + value.slice(from), selStart: pos, selEnd: pos };
    }
    const pos = selStart + INDENT.length;
    return { text: value.slice(0, selStart) + INDENT + value.slice(selEnd), selStart: pos, selEnd: pos };
};

/**
 * マーカー直後での Backspace はマーカーだけを消す（インデントは残す）
 * @returns {{text, selStart, selEnd}|null} null のときは通常の削除
 */
export const listBackspace = (value, selStart, selEnd) => {
    const item = caretListItem(value, selStart, selEnd);
    if (!item || selStart !== item.markerEnd) return null;
    const pos = item.lineStart + item.indent.length;
    return { text: value.slice(0, pos) + value.slice(item.markerEnd), selStart: pos, selEnd: pos };
};

// 整形ツールバーの定義（デザインの MD_TOOLS からインラインコードを除いた並び）
export const MD_TOOLS = [
    { icon: 'type-bold', title: '太字', kind: 'bold', shortcut: 'B' },
    { text: 'H1', title: '見出し1', kind: 'h', level: 1 },
    { text: 'H2', title: '見出し2', kind: 'h', level: 2 },
    { text: 'H3', title: '見出し3', kind: 'h', level: 3 },
    { text: 'H4', title: '見出し4', kind: 'h', level: 4 },
    { text: 'H5', title: '見出し5', kind: 'h', level: 5 },
    { text: 'H6', title: '見出し6', kind: 'h', level: 6 },
    { icon: 'list-ul', title: '箇条書き', kind: 'bullet' },
    { icon: 'list-ol', title: '番号付きリスト', kind: 'numbered' },
    { icon: 'code-square', title: 'コードブロック', kind: 'codeblock' },
    { icon: 'link-45deg', title: 'リンク', kind: 'link', shortcut: 'K' },
    // requires: PRizmo がその書き方を解釈するプロンプトだけに出す（promptLint の markupChecksOf の項目）
    { icon: 'headset', title: '有人引き継ぎリンク', kind: 'inquiry', requires: 'link' },
    { icon: 'image', title: '画像タグ', kind: 'img', requires: 'img' },
];

// 1行の見出しレベルを差し替える（既存の # は積み重ねない）
export const setHeadingLevel = (line, level) => {
    const lv = Math.min(6, Math.max(1, Math.trunc(Number(level)) || 1));
    const body = String(line ?? '').replace(/^#{1,6}(?:[ \t]+|$)/, '');
    return '#'.repeat(lv) + ' ' + body;
};

/**
 * 見出しを順に拾う（コードフェンス内は除く）
 * @returns {Array<{level, text, line, index}>} line は0始まり、index は行頭の文字位置
 */
export const parseHeadings = (text) => {
    const out = [];
    let index = 0;
    let inFence = false;
    normalizeNewlines(text).split('\n').forEach((ln, line) => {
        if (FENCE_PATTERN.test(ln)) {
            inFence = !inFence;
        } else if (!inFence) {
            const m = ln.match(HEADING_PATTERN);
            if (m) out.push({ level: m[1].length, text: (m[2] || '').trim(), line, index });
        }
        index += ln.length + 1;
    });
    return out;
};

/**
 * 整形ツールを適用した結果を返す（純関数）
 * @returns {{text, selStart, selEnd}}
 */
export const applyMdTool = (value, selStart, selEnd, tool) => {
    const val = String(value ?? '');
    const s = selStart;
    const e = selEnd;

    const wrap = (before, after, placeholder) => {
        const sel = val.slice(s, e) || placeholder;
        return {
            text: val.slice(0, s) + before + sel + after + val.slice(e),
            selStart: s + before.length,
            selEnd: s + before.length + sel.length,
        };
    };

    // 選択範囲を含む行全体に fn を適用する（キャレットだけでもその行が対象）
    const mapLines = (fn) => {
        const from = val.lastIndexOf('\n', s - 1) + 1;
        let to = e;
        if (to > from && val[to - 1] === '\n') to -= 1;
        if (to < from) to = from;
        const nl = val.indexOf('\n', to);
        to = nl < 0 ? val.length : nl;
        let n = 0;
        const lines = val.slice(from, to).split('\n');
        const block = lines.map((ln) => fn(ln, (n += 1), lines)).join('\n');
        return { text: val.slice(0, from) + block + val.slice(to), selStart: from, selEnd: from + block.length };
    };

    // すべて同じ種別のリストなら外し、そうでなければその種別にそろえる（既存の箇条書きのマーカーは残す）
    const toggleList = (kind, toItem) => mapLines((ln, n, lines) => {
        if (lines.every((l) => listKindOf(l) === kind)) return stripListMarker(ln);
        if (kind === 'bullet' && listKindOf(ln) === 'bullet') return ln;
        return toItem(stripListMarker(ln), n);
    });

    switch (tool.kind) {
        case 'bold': return wrap('**', '**', '太字');
        case 'codeblock': return wrap('```\n', '\n```', 'code');
        case 'link': return wrap('[', '](URL)', 'TEXT');
        case 'inquiry': return wrap('[', '](inquiry://open)', 'TEXT');
        case 'img': return wrap('@@img(', ')', 'URL');
        case 'h': return mapLines((ln) => setHeadingLevel(ln, tool.level));
        // ツールバーで付けるマーカーは - に統一する
        case 'bullet': return toggleList('bullet', (body) => '- ' + body);
        case 'numbered': return toggleList('numbered', (body, n) => n + '. ' + body);
        default: return { text: val, selStart: s, selEnd: e };
    }
};
